<script setup>
// 投票確認彈窗，版面對齊設計稿 assets/img/投票彈窗.png（稿寬 1440、彈窗 500×565）。
// 下面的尺寸、色票與間距都是量稿得來的值，要調整前先回頭對稿。
//
// 投票挺步道列表與步道詳情兩處的投票鈕一律先開這個彈窗，按下「確認投下N票」
// 才真的送出，因此送出的動作與結果提示都留在呼叫端，這裡只負責問「幾票、要不要投」。
//
// 張數上限就是該分區今天還能投的票數（remaining），不是寫死的：
// 同一條步道可以重複投，一區有幾票就能一次全押給眼前這條。
// 只有一票時加減兩顆自然都是禁用的——那是配額決定的，不是這裡鎖的。
import deco from '~/assets/img/vote-dlg-deco.png'
import minusIcon from '~/assets/img/vote-dlg-minus.png'
import plusIcon from '~/assets/img/vote-dlg-plus.png'
import warnIcon from '~/assets/img/vote-dlg-warn.png'
import pinIcon from '~/assets/img/icon-pin.png'

const props = defineProps({
  open: { type: Boolean, default: false },
  trailName: { type: String, default: '' },
  // 步道封面（圖片陣列的第一張），呼叫端已補好 API 網域
  cover: { type: String, default: '' },
  zoneName: { type: String, default: '' },
  // 這一區今天還能投的票數；拿不到配額（未登入、介面掛掉）時退回 1，版面才不會停在 0
  remaining: { type: Number, default: 1 },
  submitting: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'confirm'])

const { lockScroll } = useScrollLock()

// 拿不到配額（未登入、介面掛掉）時退回 1，版面才不會停在 0
const remainingVotes = computed(() => Math.max(1, Number(props.remaining) || 1))
const max = remainingVotes
const count = ref(1)

const canMinus = computed(() => count.value > 1)
const canPlus = computed(() => count.value < max.value)

function step(n) {
  count.value = Math.min(max.value, Math.max(1, count.value + n))
}

function close() {
  // 送出中不讓關：關掉了那一票還是會進去，畫面與結果會對不起來
  if (props.submitting) return
  emit('close')
}

function onKeydown(e) {
  if (e.key === 'Escape') close()
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      // 每次打開都回到預設值 1：上一條步道選的張數不該留給下一條
      count.value = 1
      window.addEventListener('keydown', onKeydown)
      lockScroll(true)
      return
    }
    window.removeEventListener('keydown', onKeydown)
    lockScroll(false)
  }
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="vd">
        <div v-if="open" class="vd-mask" @click.self="close()">
          <div class="vd-panel" role="dialog" aria-modal="true" aria-labelledby="vd-title">
            <div class="vd-head">
              <img class="vd-deco" :src="deco" width="134" height="50" alt="" aria-hidden="true" />
              <h2 id="vd-title" class="vd-title">
                <span>給</span>
                <b>{{ trailName }}</b>
                <span>投票</span>
              </h2>
            </div>

            <div class="vd-cover">
              <img v-if="cover" :src="cover" :alt="trailName" />
            </div>

            <p v-if="zoneName" class="vd-zone">
              <img :src="pinIcon" width="12" height="14" alt="" aria-hidden="true" />
              {{ zoneName }}
            </p>

            <div class="vd-step">
              <button
                class="vd-step-btn"
                type="button"
                :disabled="!canMinus"
                aria-label="減少一票"
                @click="step(-1)"
              >
                <img :src="minusIcon" width="20" height="20" alt="" aria-hidden="true" />
              </button>
              <output class="vd-count" aria-live="polite">{{ count }}</output>
              <button
                class="vd-step-btn"
                type="button"
                :disabled="!canPlus"
                aria-label="增加一票"
                @click="step(1)"
              >
                <img :src="plusIcon" width="20" height="20" alt="" aria-hidden="true" />
              </button>
            </div>

            <p class="vd-quota">
              您今天在{{ zoneName || '本區' }}還可使用 <b>{{ remainingVotes }}</b> 票
            </p>

            <p class="vd-warn">
              <img :src="warnIcon" width="17" height="17" alt="" aria-hidden="true" />
              <span>送出後即視為完成投票，今日剩餘票數將扣除對應張數，無法修改或取消</span>
            </p>

            <div class="vd-acts">
              <button
                class="vd-btn vd-cancel"
                type="button"
                :disabled="submitting"
                @click="close()"
              >
                取消
              </button>
              <button
                class="vd-btn vd-confirm"
                type="button"
                :disabled="submitting"
                @click="emit('confirm', count)"
              >
                {{ submitting ? '投票中…' : `確認投下${count}票` }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.vd-mask {
  position: fixed;
  inset: 0;
  z-index: 210;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* 稿上白色頁首被壓成 #7f7f7f，反推遮罩就是五成黑（與登入彈窗一致） */
  background: rgba(0, 0, 0, 0.5);
}

.vd-panel {
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 48px);
  padding: 0 30px 38px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(var(--site-shadow-rgb), 0.28);
  text-align: center;
  overflow-y: auto;
}

/* ---- 標題（山形裝飾疊在文字後面） ---- */

.vd-head {
  position: relative;
  padding-top: 34px;
}

.vd-deco {
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%);
  display: block;
  pointer-events: none;
}

.vd-title {
  position: relative;
  margin: 0;
  color: #000000;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.55;
}

.vd-title b {
  margin: 0 14px;
  font-weight: 700;
}

/* ---- 步道封面 ---- */

.vd-cover {
  width: 120px;
  height: 120px;
  margin: 18px auto 0;
  border: 3px solid #c8f2ca;
  border-radius: 50%;
  background: #eefaf0;
  overflow: hidden;
}

.vd-cover img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.vd-zone {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  margin: 14px 0 0;
  padding: 0 10px;
  border: 1px solid #68daab;
  border-radius: 999px;
  background: #e7fcf6;
  color: #159a8d;
  font-size: 14px;
  line-height: 1;
}

/* ---- 張數加減 ---- */

.vd-step {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  margin-top: 18px;
  padding: 0 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #b9f7d4 0%, #fbfdfd 50%, #fbe1c5 100%);
}

.vd-step-btn {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(var(--site-shadow-rgb), 0.12);
  cursor: pointer;
  transition:
    transform 0.2s,
    opacity 0.2s;
}

.vd-step-btn img {
  display: block;
}

.vd-step-btn:hover:not(:disabled) {
  transform: scale(1.06);
}

/* 加到頂或減到底就沒得按，設計稿指定的禁用樣式就是半透明 */
.vd-step-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.vd-count {
  flex: 1;
  color: #000000;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  /* 數字等寬，1 換成 10 時左右兩顆按鈕不會被推著跑 */
  font-variant-numeric: tabular-nums;
}

.vd-quota {
  margin: 18px 0 0;
  color: #111111;
  font-size: 14px;
  line-height: 1.6;
}

.vd-quota b {
  color: #ff383c;
  font-size: 16px;
  font-weight: 700;
}

/* ---- 注意事項 ---- */

.vd-warn {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 18px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #eb71281a;
  color: #333333;
  font-size: 14px;
  line-height: 1.6;
  text-align: left;
}

.vd-warn img {
  flex: none;
  display: block;
}

/* ---- 動作按鈕 ---- */

.vd-acts {
  display: flex;
  gap: 18px;
  margin-top: 18px;
}

.vd-btn {
  flex: 1;
  height: 56px;
  padding: 0;
  border-radius: 999px;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition:
    filter 0.25s,
    background-color 0.25s,
    opacity 0.25s;
}

.vd-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.vd-cancel {
  border: 1px solid #99999999;
  background: #fff;
  color: #3c4043;
}

.vd-cancel:hover:not(:disabled) {
  background: #f6f6f6;
}

.vd-confirm {
  border: 0;
  background: linear-gradient(90deg, #fdca56 0%, #f38c39 54.33%, #fb939c 100%);
  color: #fff;
}

.vd-confirm:hover:not(:disabled) {
  filter: brightness(1.05);
}

/* ---- 進出場 ---- */

.vd-enter-active,
.vd-leave-active {
  transition: opacity 0.25s;
}

.vd-enter-from,
.vd-leave-to {
  opacity: 0;
}

.vd-enter-active .vd-panel,
.vd-leave-active .vd-panel {
  transition: transform 0.25s;
}

.vd-enter-from .vd-panel,
.vd-leave-to .vd-panel {
  transform: translateY(16px);
}

@media (prefers-reduced-motion: reduce) {
  .vd-enter-active,
  .vd-leave-active,
  .vd-enter-active .vd-panel,
  .vd-leave-active .vd-panel,
  .vd-step-btn {
    transition: none;
  }
}

@media (max-width: 560px) {
  .vd-mask {
    padding: 16px;
  }

  .vd-panel {
    padding: 0 20px 26px;
    border-radius: 20px;
  }

  .vd-title {
    font-size: 18px;
  }

  .vd-title b {
    margin: 0 6px;
  }

  .vd-cover {
    width: 100px;
    height: 100px;
    margin-top: 12px;
  }

  .vd-step {
    height: 62px;
    margin-top: 14px;
    padding: 0 10px;
  }

  .vd-step-btn {
    width: 42px;
    height: 42px;
  }

  .vd-count {
    font-size: 32px;
  }

  .vd-quota,
  .vd-warn {
    margin-top: 14px;
    font-size: 13px;
  }

  .vd-acts {
    gap: 12px;
    margin-top: 14px;
  }

  .vd-btn {
    height: 50px;
    font-size: 16px;
  }
}
</style>
